import { CheckCircle, ShieldAlert } from 'lucide-react';

import { Alert } from '@@/Alert';
import { Icon } from '@@/Icon';
import { InlineLoader } from '@@/InlineLoader';
import { Link } from '@@/Link';

import { AddonChartSourceStatus } from '../queries/useAddonChartSourceStatus';

/**
 * ChartSourceStatus reports whether Portainer can actually read the chart the
 * form is pointing at, next to the fields that choose it.
 *
 * It is the difference between a chart location that is wrong and one that is
 * unreadable — an air-gapped mirror serving a certificate no public CA signed
 * reports neither versions nor a reason on its own, and the form would otherwise
 * look merely empty. Where it cannot be read, the remedy is offered with it: a
 * registry configured in Portainer carries the credentials and TLS settings the
 * anonymous pull lacks.
 */
export function ChartSourceStatus({
  status,
  isLoading,
  canChangeSource,
  warnUnverified,
}: {
  status?: AddonChartSourceStatus;
  isLoading?: boolean;
  // The source is fixed once the add-on is installed, so the detail view reports
  // the state without offering a way to repoint it.
  canChangeSource?: boolean;
  // Whether a certificate accepted without verifying it is worth saying. A
  // registry configured in Portainer carries its own "skip certificate
  // verification" setting, chosen for that registry by the administrator reading
  // this — repeating it back is noise. A catalog entry's flag is a default of a
  // document, and the chart it points at may well be a public one.
  warnUnverified?: boolean;
}) {
  if (isLoading) {
    return (
      <InlineLoader className="mt-2">
        Checking the chart location...
      </InlineLoader>
    );
  }

  if (!status) {
    return null;
  }

  if (status.reachable) {
    return <ReachableStatus tlsVerify={status.tlsVerify || !warnUnverified} />;
  }

  return (
    <Alert color="warn" title="Chart location not reachable" className="mt-2">
      <p className="mb-0">{status.reason}</p>
      <p className="mb-0">{canChangeSource ? <Repoint /> : <FixedSource />}</p>
    </Alert>
  );
}

// The remedy while the source can still be chosen: a registry configured in
// Portainer carries the credentials and TLS settings an anonymous pull lacks.
function Repoint() {
  return (
    <>
      Pull the chart from a registry configured in Portainer instead where its
      credentials and TLS settings are used for the pull. Select it as the chart
      source above or <RegistriesLink>add one</RegistriesLink>.
    </>
  );
}

// A running release keeps the source it was installed from, so the way out is
// to fix that registry rather than to point somewhere else.
function FixedSource() {
  return (
    <>
      The chart source cannot be changed once the add-on is installed. Check the
      registry holding this chart in{' '}
      <RegistriesLink>registry management</RegistriesLink>.
    </>
  );
}

// Opens beside the install: a registry is added without navigating away from a
// half-filled form.
function RegistriesLink({ children }: { children: string }) {
  return (
    <Link
      to="portainer.registries"
      target="_blank"
      rel="noreferrer"
      data-cy="addon-chart-source-registries-link"
    >
      {children}
    </Link>
  );
}

function ReachableStatus({ tlsVerify }: { tlsVerify: boolean }) {
  if (!tlsVerify) {
    return (
      <span
        className="mt-2 inline-flex items-center gap-1 text-sm text-warning-8 th-highcontrast:text-white th-dark:text-warning-5"
        data-cy="addon-chart-source-unverified"
      >
        <Icon icon={ShieldAlert} />
        Chart location reachable, but its certificate is not verified
      </span>
    );
  }

  return (
    <span
      className="mt-2 inline-flex items-center gap-1 text-sm text-green-8 th-highcontrast:text-white th-dark:text-green-3"
      data-cy="addon-chart-source-reachable"
    >
      <Icon icon={CheckCircle} />
      Chart location reachable
    </span>
  );
}
